import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { Icon } from '@eventbrite/eds-icon';
import { ArrowRightChunky } from '@eventbrite/eds-iconography';
import {
    EventCardShareAction,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import React from 'react';
import { connect } from 'react-redux';
import DiscoverVerticalBrowseHierarchyEventCard from '../../../../components/DiscoverEventCard/Vertical/DiscoverVerticalBrowseHierarchyEventCard';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import { getLocationInfo } from '../../../../utils/venue';
import {
    HOMEPAGE_HOLIDAY_FEATURED,
    UtmOptions,
} from '../../constants/analytics';
import { SEASONAL_HOLIDAY_FEATURED } from '../../constants/seasonalFeatured';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import { clickOnViewEvent } from '../../redux/events';
import './HolidayFeaturedContainer.scss';

interface Event extends FormattedEvent {
    hideStartDate?: boolean;
    numFollowers?: number;
    venueName?: string;
    venueAddress?: string;
}

interface HolidayFeaturedProps extends ReduxProps {
    holiday: string;
}
interface TitleSection {
    city: string;
    holiday: string;
}

const utmOptions: UtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-term': 'listing',
    'utm-source': 'cp',
};

const TitleDescription = React.memo(({ city, holiday }: TitleSection) => (
    <div className="title-subtitle-container">
        <h2>{SEASONAL_HOLIDAY_FEATURED[holiday].title}</h2>
        <h3>{SEASONAL_HOLIDAY_FEATURED[holiday].subTitle}</h3>
        <h4>{SEASONAL_HOLIDAY_FEATURED[holiday].description}</h4>
        <a href={SEASONAL_HOLIDAY_FEATURED[holiday].viewMoreUrl}>
            Explore {city} events
            <Icon type={<ArrowRightChunky />} />
        </a>
    </div>
));

export const HolidayFeatured = ({
    isAuthenticated,
    isMobile,
    location,
    onViewEvent,
    holiday,
    shareOptions,
}: HolidayFeaturedProps) => {
    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'HomepageHolidayFeaturedView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            page_size: 8,
            tags: SEASONAL_HOLIDAY_FEATURED[holiday].categoryTag,
            aggs: {},
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });
    if (isLoading) {
        return (
            <div className="holiday-featured-events">
                <LoadingSkeleton height={isMobile ? '515px' : '364px'} />
            </div>
        );
    }

    if (!data || !data?.events || data.events.length == 0) return null;
    const events: Event[] = data?.events || [];

    let cardsComponent: React.ReactElement[] = [];

    // Custom bucket key for tracking analytics
    const bucketKey = 'holiday_featured_events';

    cardsComponent = events.map(
        ({
            id,
            isFree,
            isSoldOut,
            startDate,
            hideStartDate,
            numFollowers,
            venueName,
            venueAddress,
            ...cardProps
        }) => {
            let locationInfoValue = getLocationInfo(venueName, venueAddress);
            if (typeof locationInfoValue !== 'string') {
                locationInfoValue = '';
            }

            return (
                <div className="holiday-featured-events-event-card" key={id}>
                    <DiscoverVerticalBrowseHierarchyEventCard
                        event={{
                            ...cardProps,
                            id: id || '',
                            isFree: isFree || false,
                            isSoldOut: isSoldOut,
                            startDate: hideStartDate ? '' : startDate,
                            organizerFollowerCount: numFollowers,
                        }}
                        onClick={onViewEvent?.bind(
                            null,
                            id,
                            bucketKey,
                            numFollowers,
                        )}
                        moreActions={
                            <EventCardShareAction
                                shareOptions={{
                                    ...shareOptions,
                                    eventId: id,
                                    eventName: cardProps.name,
                                    eventUrl: cardProps.url,
                                    utmOptions,
                                    trackingCategory: 'eventcard',
                                }}
                            />
                        }
                        isAuthenticated={isAuthenticated || false}
                        affCode="ehomefeatured"
                        isLazyImage={true}
                        statsigLocationString={HOMEPAGE_HOLIDAY_FEATURED}
                        locale="en_US"
                    />
                </div>
            );
        },
    );

    return (
        <div className="holiday-featured-events">
            <div className="holiday-featured-events-container">
                <TitleDescription
                    city={location.currentPlace || ''}
                    holiday={holiday}
                />
                <div className="simple-carousel-parent">
                    <SimpleCarousel useSideNavigation={true}>
                        {cardsComponent}
                    </SimpleCarousel>
                </div>
            </div>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
    };
};

const _mapDispatchToProps = {
    onViewEvent: clickOnViewEvent,
};
type ReduxProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;
export default connect(_mapStateToProps, _mapDispatchToProps)(HolidayFeatured);
